import { Tag } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  ColumnSelectHeader,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { UserEntity, UserTypeEnum } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadUsers,
  loadUserTypes,
  setAction,
  setSelectedUser,
  setShowSearchDrawer,
  setUserFilter,
} from '../../../stores/actions'
import {
  ManageUserModalComponent,
  ResetPasswordModalComponent,
  SearchUserDrawerComponent,
} from './components'

export const UserPage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { profile } = useSelector((state: RootState) => state.auth)
  const { management } = useSelector((state: RootState) => state.common)
  const { users, isUsersLoaded, userTypes, userFilter } = useSelector(
    (state: RootState) => state.user
  )
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const [filter, setFilter] = React.useState(userFilter)

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
    // eslint-disable-next-line
  }, [isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadUsers())
    dispatch(loadUserTypes())
    // eslint-disable-next-line
  }, [])

  React.useEffect(() => {
    dispatch(
      setUserFilter({
        ...userFilter,
        userTypes: userTypes.filter((item) =>
          profile && profile.userType.type === UserTypeEnum.ADMIN
            ? item.type !== UserTypeEnum.ADMIN
            : item.type === UserTypeEnum.CUSTOMER
        ),
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTypes])

  React.useEffect(() => {
    setFilter(userFilter)
  }, [userFilter])

  const filteredUsers = React.useMemo(() => {
    const genders = userFilter.genders || ['true', 'false']
    const typeOfUsers = userFilter.userTypes || []
    return users
      ? users.filter(
          (user) =>
            user.name
              .toLowerCase()
              .includes((userFilter.name || '').trim().toLowerCase()) &&
            user.username
              .toLowerCase()
              .includes(
                (userFilter.username || '').trim().toLocaleLowerCase()
              ) &&
            user.phone.includes((userFilter.phone || '').trim()) &&
            (!genders.length || genders.includes(user.gender.toString())) &&
            user.userType.type !== UserTypeEnum.ADMIN &&
            (!typeOfUsers.length ||
              typeOfUsers.filter((item) => item.id === user.userType.id).length)
        )
      : []
  }, [users, userFilter])

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: translate('user.id', language),
        width: 30,
      },
      {
        dataIndex: 'name',
        title: (
          <ColumnInputHeader
            titleKey="user.name"
            value={filter.name}
            onChange={(e) => setFilter({ ...filter, name: e.target.value })}
          />
        ),
        width: 130,
      },
      {
        dataIndex: 'username',
        title: (
          <ColumnInputHeader
            titleKey="user.username"
            value={filter.username}
            onChange={(e) => setFilter({ ...filter, username: e.target.value })}
          />
        ),
        width: 130,
      },
      {
        dataIndex: 'phone',
        title: (
          <ColumnInputHeader
            titleKey="common.phone"
            value={filter.phone}
            onChange={(e) => setFilter({ ...filter, phone: e.target.value })}
          />
        ),
        width: 130,
      },
      {
        title: (
          <ColumnSelectHeader
            titleKey="common.gender"
            values={filter.genders}
            onChange={(e) =>
              setFilter({
                ...filter,
                genders: e ? e.map((item: any) => item.toString()) : [],
              })
            }
            options={[true, false].map((item) => ({
              value: item.toString(),
              name: translate(
                `common.gender.${item ? 'male' : 'female'}`,
                language
              ),
            }))}
          />
        ),
        render: (_: any, user: UserEntity) =>
          translate(
            `common.gender.${user.gender ? 'male' : 'female'}`,
            language
          ),
        width: 150,
      },
      {
        title: (
          <ColumnSelectHeader
            titleKey="user.userType"
            values={filter.userTypes.map((item) => item.id.toString())}
            onChange={(e: any[]) =>
              setFilter({
                ...filter,
                userTypes: userTypes.filter((item) =>
                  e.includes(item.id.toString())
                ),
              })
            }
            options={userTypes
              .filter((item) =>
                profile && profile.userType.type === UserTypeEnum.ADMIN
                  ? item.type !== UserTypeEnum.ADMIN
                  : item.type === UserTypeEnum.CUSTOMER
              )
              .map((item) => ({
                value: item.id.toString(),
                name: translate(
                  `user.userType.${item.type.toLowerCase()}`,
                  language
                ),
              }))}
          />
        ),
        render: (_: any, user: UserEntity) => (
          <Tag
            style={{ fontSize: '0.7rem' }}
            color={
              user.userType.type === UserTypeEnum.ADMIN
                ? 'green'
                : user.userType.type === UserTypeEnum.CUSTOMER
                ? 'blue'
                : 'red'
            }
            key={user.userType.type}
          >
            {translate(
              `user.userType.${user.userType.type.toLowerCase()}`,
              language
            )}
          </Tag>
        ),
        width: 150,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => dispatch(setUserFilter(filter))}
          />
        ),
        render: (_: any, user: UserEntity) =>
          user.userType.type !== UserTypeEnum.ADMIN && (
            <TableActionColumnButtonComponent
              onViewButtonClick={() => {
                dispatch(setSelectedUser(user))
                dispatch(setAction(ActionEnum.VIEW))
              }}
              onDeleteButtonClick={() => {
                dispatch(setSelectedUser(user))
                dispatch(setAction(ActionEnum.DELETE))
              }}
              phone={user.phone}
            />
          ),
        width: 200,
      },
    ],
    [language, filter, userTypes, profile, dispatch]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showResetPasswordModal && <ResetPasswordModalComponent />}
      {management.showManageModal && <ManageUserModalComponent />}
      <PageTitleComponent
        onCreateButtonClick={() => {
          dispatch(setSelectedUser())
          dispatch(setAction(ActionEnum.CREATE))
        }}
        hasSearchDrawer
      />
      {management.showSearchDrawer && <SearchUserDrawerComponent />}
      <TableComponent
        data={filteredUsers}
        columns={columns}
        loading={!isUsersLoaded}
      />
    </div>
  )
}
